import {
  ActionBar,
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  ResponsiveList,
  Skeleton,
  StatusBadge,
  useMediaQuery,
  useToast,
  type ResponsiveListColumn,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  Building2,
  CalendarClock,
  LayoutDashboard,
  LockKeyhole,
  PackageCheck,
  RefreshCw,
  Send,
  type LucideIcon,
} from 'lucide-react';
import { type JSX, useState } from 'react';

import { useCurrentUser } from '../../auth/use-current-user';
import { formatCalendarDate } from '../../orders/format-calendar-date';
import { mapOrderError } from '../../orders/order-api-errors';
import { OperatingUnitSelect } from '../../components/operating-unit-select';
import { GuideLink } from '../../guide/guide-link';
import {
  closeCutoff,
  getDayStatus,
  remindMissingOrders,
  type DayStatusEntry,
} from '../../orders/api';
import { useOperatingUnitOptions } from '../../operating-units/use-operating-unit-options';
import { hasPermission } from '../../auth/has-permission';
import { isAutoCloseEnabled } from '../settings/system-parameters/auto-close-setting';
import { listSettings } from '../settings/system-parameters/system-parameters.api';
import { DeviationAlertBadge } from './components/deviation-alert-badge';

/**
 * Table columns (≥640px): row rendering only, not reused by the card layout. The point-of-sale
 * column only exists for an actor who sees several points (`design.md` §9).
 */
function buildBoardColumns(showUnit: boolean): ResponsiveListColumn<DayStatusEntry>[] {
  return [
    { key: 'company', header: 'Empresa', render: (entry) => entry.customerName },
    { key: 'site', header: 'Sede', render: (entry) => entry.siteName ?? '—' },
    ...(showUnit
      ? [
          {
            key: 'operatingUnit',
            header: 'Punto de venta',
            render: (entry: DayStatusEntry) => entry.operatingUnitName ?? '—',
          },
        ]
      : []),
    {
      key: 'status',
      header: 'Estado',
      render: (entry) =>
        entry.hasOrder && entry.status !== null ? (
          <div className="flex flex-col items-start gap-1">
            <StatusBadge domain="order" status={entry.status} />
            {entry.closedAutomatically ? (
              <span className="text-xs text-slate-500">Cierre automático</span>
            ) : null}
          </div>
        ) : (
          <span className="text-sm text-slate-500">Sin pedido</span>
        ),
    },
    { key: 'quantity', header: 'Cantidad', render: (entry) => entry.totalQuantity },
    {
      key: 'alerts',
      header: 'Alertas',
      render: (entry) => (
        <div className="flex flex-col gap-1">
          <DeviationAlertBadge deviation={entry.deviation} />
          {entry.isBelowMinimum ? (
            <span className="text-xs font-medium text-amber-700">Bajo el mínimo</span>
          ) : null}
        </div>
      ),
    },
  ];
}

const dashboardRoute = getRouteApi('/protected/');

const REQUEST_ADMIN_HINT = 'No tenés permiso para esta acción. Solicitalo a un administrador.';

type DashboardView = 'all' | 'missing' | 'draft' | 'alerts';

/** `day-board/spec.md` → labels shown next to "Mostrando:" when a KPI filter is active. */
const VIEW_LABELS: Record<Exclude<DashboardView, 'all'>, string> = {
  missing: 'Faltantes',
  draft: 'Enviados / Borradores',
  alerts: 'Alertas',
};

interface SummaryCardProps {
  icon: LucideIcon;
  label: string;
  value: string;
  tone?: 'default' | 'warning';
  /** When set, the whole card becomes a link that sets `view` on `/` (design.md §6: client-side filter, no `/orders` navigation). */
  view?: Exclude<DashboardView, 'all'>;
}

/** `day-board/spec.md` → "resumen del día": one glanceable number per card, `tabular-nums` so the digits don't jitter across the 30s polling refresh. */
function SummaryCard({
  icon: Icon,
  label,
  value,
  tone = 'default',
  view,
}: SummaryCardProps): JSX.Element {
  const content = (
    <Card className="flex items-center gap-3">
      <span
        className={
          tone === 'warning'
            ? 'flex size-10 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600'
            : 'flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600'
        }
      >
        <Icon aria-hidden="true" className="size-5" />
      </span>
      <div className="flex flex-col">
        <span className="text-xs font-medium text-slate-500">{label}</span>
        <span className="text-xl font-semibold tabular-nums text-slate-900">{value}</span>
      </div>
    </Card>
  );

  if (view === undefined) {
    return content;
  }

  return (
    <Link
      to="/"
      search={(prev) => ({ ...prev, view })}
      data-testid={`kpi-card-${view}`}
      className="block rounded-xl text-left"
    >
      {content}
    </Link>
  );
}

/**
 * `day-board/spec.md` → "Pantalla del Tablero del día en back-office".
 * Polling with `refetchInterval: 30_000` plus a manual refresh button
 * (`orders-module/design.md` §8.2: no WebSocket in this phase). The date
 * lives in the URL, default today, same convention as every other list
 * screen's filters.
 */
export function DashboardDayPage(): JSX.Element {
  const search = dashboardRoute.useSearch();
  const navigate = dashboardRoute.useNavigate();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  // The route's URL search params live in `router.tsx`; this filter stays local state.
  const [operatingUnitId, setOperatingUnitId] = useState('');
  const unitOptions = useOperatingUnitOptions();
  const showUnit = unitOptions.length > 1;
  const selectedUnitName = unitOptions.find((unit) => unit.id === operatingUnitId)?.name;
  const unitFilter = operatingUnitId === '' ? undefined : operatingUnitId;

  const query = useQuery({
    queryKey: ['day-status', search.serviceDate, operatingUnitId],
    queryFn: () => getDayStatus(search.serviceDate, unitFilter),
    refetchInterval: 30_000,
  });

  // Needs `settings.read`; without it the hint is simply not shown.
  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: listSettings,
    retry: false,
    staleTime: 60_000,
  });
  const autoCloseOn =
    Array.isArray(settingsQuery.data) ? isAutoCloseEnabled(settingsQuery.data) : undefined;

  const canClose = hasPermission(currentUser.data, 'order.close.cutoff');
  const canRemind =
    hasPermission(currentUser.data, 'order.read.all') &&
    (hasPermission(currentUser.data, 'order.create') ||
      hasPermission(currentUser.data, 'order.update'));
  const isDesktop = useMediaQuery('(min-width: 640px)');

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['day-status'] });
  }

  const closeMutation = useMutation({
    mutationFn: () => closeCutoff(search.serviceDate, unitFilter),
    onSuccess: async (result) => {
      await invalidate();
      setCloseConfirmOpen(false);
      toast(
        'success',
        `Corte cerrado: ${result.confirmedCount} confirmados, ${result.cancelledCount} cancelados`,
      );
    },
  });

  const remindMutation = useMutation({
    mutationFn: () => remindMissingOrders(search.serviceDate),
    onSuccess: (result) => {
      toast('success', `Se registraron ${result.remindedCount} recordatorios`);
    },
    onError: (error) => {
      toast('error', mapOrderError(error));
    },
  });

  function changeDate(serviceDate: string): void {
    void navigate({ search: (prev) => ({ ...prev, serviceDate }) });
  }

  const entries = query.data?.entries ?? [];
  const missingCount = entries.filter((entry) => !entry.hasOrder).length;
  const totalUnits = entries.reduce((sum, entry) => sum + entry.totalQuantity, 0);
  const submittedCount = entries.filter((entry) => entry.status === 'SUBMITTED').length;
  const draftCount = entries.filter((entry) => entry.status === 'DRAFT').length;
  const alertCount = entries.filter(
    (entry) => entry.isBelowMinimum || (entry.deviation?.isDeviating ?? false),
  ).length;

  const view = search.view;
  const visibleEntries =
    view === 'missing'
      ? entries.filter((entry) => !entry.hasOrder)
      : view === 'draft'
        ? entries.filter((entry) => entry.status === 'DRAFT' || entry.status === 'SUBMITTED')
        : view === 'alerts'
          ? entries.filter(
              (entry) => entry.isBelowMinimum || (entry.deviation?.isDeviating ?? false),
            )
          : entries;

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={LayoutDashboard}
        title="Tablero del día"
        description="Mirá qué empresas ya pidieron y el estado del corte antes de cerrarlo."
        actions={
          <div className="flex items-center gap-2">
            <GuideLink moduleId="orders" />
            {isDesktop ? (
              <>
                <Button variant="secondary" icon={RefreshCw} onClick={() => void query.refetch()}>
                  Actualizar
                </Button>
                <Button
                  variant="secondary"
                  icon={CalendarClock}
                  loading={remindMutation.isPending}
                  disabled={!canRemind || missingCount === 0}
                  title={!canRemind ? REQUEST_ADMIN_HINT : undefined}
                  onClick={() => remindMutation.mutate()}
                >
                  Recordar a los que faltan
                </Button>
                <Button
                  icon={LockKeyhole}
                  disabled={!canClose}
                  title={!canClose ? REQUEST_ADMIN_HINT : undefined}
                  onClick={() => setCloseConfirmOpen(true)}
                >
                  Cerrar corte
                </Button>
              </>
            ) : (
              <ActionBar
                primary={
                  canClose
                    ? {
                        label: 'Cerrar corte',
                        icon: LockKeyhole,
                        onClick: () => setCloseConfirmOpen(true),
                      }
                    : {
                        label: 'Actualizar',
                        icon: RefreshCw,
                        variant: 'secondary',
                        onClick: () => void query.refetch(),
                      }
                }
                secondary={[
                  ...(canClose
                    ? [
                        {
                          label: 'Actualizar',
                          icon: RefreshCw,
                          variant: 'secondary' as const,
                          onClick: () => void query.refetch(),
                        },
                      ]
                    : []),
                  ...(canRemind
                    ? [
                        {
                          label: 'Recordar a los que faltan',
                          icon: CalendarClock,
                          variant: 'secondary' as const,
                          disabled: missingCount === 0,
                          onClick: () => remindMutation.mutate(),
                        },
                      ]
                    : []),
                ]}
              />
            )}
          </div>
        }
      />

      {autoCloseOn === undefined ? null : (
        <p className="text-sm text-slate-600">
          {autoCloseOn
            ? 'Cierre automático activo — cada pedido cierra a su hora de corte'
            : 'Cierre automático desactivado — el corte se cierra a mano'}
        </p>
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-4">
          <Field
            label="Fecha de servicio"
            name="serviceDate"
            type="date"
            value={search.serviceDate}
            onChange={(event) => changeDate(event.target.value)}
          />
          <OperatingUnitSelect
            id="day-board-operating-unit"
            label="Punto de venta"
            allLabel="Todos los puntos de venta"
            value={operatingUnitId}
            onChange={setOperatingUnitId}
          />
        </div>
      </Card>

      {query.isError ? (
        <Alert tone="danger" title="No pudimos cargar el tablero">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton data-testid="day-board-skeleton" className="h-10 w-full" />
          <Skeleton data-testid="day-board-skeleton" className="h-10 w-full" />
          <Skeleton data-testid="day-board-skeleton" className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined ? (
        <div
          data-testid="day-board-summary"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          <SummaryCard
            icon={Building2}
            label="Faltantes"
            value={String(missingCount)}
            view="missing"
          />
          <SummaryCard icon={PackageCheck} label="Unidades totales" value={String(totalUnits)} />
          <SummaryCard
            icon={Send}
            label="Enviados / Borradores"
            value={`${submittedCount} / ${draftCount}`}
            view="draft"
          />
          <SummaryCard
            icon={AlertTriangle}
            label="Alertas"
            value={String(alertCount)}
            tone={alertCount > 0 ? 'warning' : 'default'}
            view="alerts"
          />
        </div>
      ) : null}

      {view !== 'all' ? (
        <p className="text-sm text-slate-600" data-testid="day-board-filter-indicator">
          Mostrando: {VIEW_LABELS[view]} ·{' '}
          <Link
            to="/"
            search={(prev) => ({ ...prev, view: 'all' as const })}
            className="font-medium text-primary-600 underline"
          >
            Ver todo
          </Link>
        </p>
      ) : null}

      {query.data !== undefined && entries.length === 0 ? (
        <EmptyState
          icon={LayoutDashboard}
          title="Ninguna empresa con contrato activo para esta fecha."
        />
      ) : null}

      {query.data !== undefined && visibleEntries.length > 0 ? (
        <ResponsiveList
          items={visibleEntries}
          rowKey={(entry) =>
            `${entry.customerId}:${entry.siteId ?? 'sin-sede'}:${entry.operatingUnitId ?? 'sin-punto'}`
          }
          columns={buildBoardColumns(showUnit)}
          renderCardHeader={(entry) => entry.customerName}
          renderCardBody={(entry) => (
            <>
              <span className="text-slate-500">Sede</span>
              <span>{entry.siteName ?? '—'}</span>
              {showUnit ? (
                <>
                  <span className="text-slate-500">Punto de venta</span>
                  <span>{entry.operatingUnitName ?? '—'}</span>
                </>
              ) : null}
              <span className="text-slate-500">Estado</span>
              {entry.hasOrder && entry.status !== null ? (
                <StatusBadge domain="order" status={entry.status} />
              ) : (
                <span className="text-sm text-slate-500">Sin pedido</span>
              )}
              <span className="text-slate-500">Cantidad</span>
              <span>{entry.totalQuantity}</span>
              <span className="text-slate-500">Alertas</span>
              <div className="flex flex-col gap-1">
                <DeviationAlertBadge deviation={entry.deviation} />
                {entry.isBelowMinimum ? (
                  <span className="text-xs font-medium text-amber-700">Bajo el mínimo</span>
                ) : null}
              </div>
            </>
          )}
          getRowTestId={(entry) => `day-board-card-${entry.customerId}`}
          caption="Resumen del día"
          emptyState={null}
        />
      ) : null}

      {closeConfirmOpen ? (
        <Modal
          open
          onClose={() => setCloseConfirmOpen(false)}
          title="Cerrar corte"
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {closeMutation.isError ? (
              <Alert tone="danger">{mapOrderError(closeMutation.error)}</Alert>
            ) : null}
            <p className="text-sm text-slate-700">
              Se van a confirmar los pedidos enviados y cancelar los que quedaron en borrador para
              el {formatCalendarDate(search.serviceDate)}
              {selectedUnitName !== undefined
                ? ` en ${selectedUnitName}`
                : showUnit
                  ? ' en todos tus puntos de venta'
                  : ''}
              . Esta acción no se puede deshacer.
            </p>
            <div className="flex items-center gap-3">
              <Button loading={closeMutation.isPending} onClick={() => closeMutation.mutate()}>
                Confirmar cierre
              </Button>
              <Button variant="secondary" onClick={() => setCloseConfirmOpen(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
